// src/components/maintenance/GestionProveedores.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { proveedorService } from '../../services/maintenance/proveedorService';
import ProveedorModal from './ProveedorModal';
import DetalleProveedor from './DetalleProveedor';
import {
    Plus,
    Search,
    Tag,
    BarChart3,
    CheckCircle2,
    Clock4,
    TrendingUp,
    Package,
    Layers,
    Award,
    Zap,
    AlertCircle,
    Building2,
    Edit3,
    RefreshCcw
} from 'lucide-react';


interface Proveedor {
    id: number;
    codigo: string;
    razon_social: string;
    ruc: string;
    contacto: string;
    telefono: string;
    email: string;
    direccion: string;
    calificacion: 'A' | 'B' | 'C';
    dias_entrega_promedio: number;
    tiempo_respuesta_promedio_horas: number;
    especializacion: string[];
    metricas: {
        total_pedidos: number;
        entregas_a_tiempo: number;
        porcentaje_puntualidad: number;
        calidad_promedio: number;
        total_comprado: number;
    };
    total_items_cotizados: number;
    total_paquetes: number;
    activo: boolean;
}

const GestionProveedores: React.FC = () => {
    // ⚡️ OPTIMIZACIÓN: Cargar datos iniciales desde LocalStorage para evitar el "Loading" al recargar
    const [proveedores, setProveedores] = useState<Proveedor[]>(() => {
        const saved = localStorage.getItem('vilox_cache_proveedores');
        return saved ? JSON.parse(saved) : [];
    });

    // Si tenemos datos en caché, empezamos sin el loading spinner
    const [loading, setLoading] = useState(proveedores.length === 0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCalificacion, setFilterCalificacion] = useState<string>('TODOS');
    const [showModal, setShowModal] = useState(false);
    const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
    const [showDetalle, setShowDetalle] = useState(false);
    const [proveedorDetalleId, setProveedorDetalleId] = useState<number | null>(null);

    // ⚡️ OPTIMIZACIÓN: Solo cargar si no hay datos o refrescar en background
    useEffect(() => {
        loadProveedores();
    }, []);

    // ⚡️ OPTIMIZACIÓN: Debounce para búsqueda local
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadProveedores = async (force: boolean = false) => {
        try {
            // Si forzamos la carga, mostramos el estado de carga en la lista
            if (proveedores.length === 0 || force) {
                setLoading(true);
            }

            const response = await proveedorService.getProveedores();
            if (response.success) {
                setProveedores(response.data);
                // ⚡️ Persistir en local para la próxima recarga
                localStorage.setItem('vilox_cache_proveedores', JSON.stringify(response.data));
            }
        } catch (error) {
            console.error('Error cargando proveedores:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        // 1. Limpiar caché en LocalStorage para este módulo
        localStorage.removeItem('vilox_cache_proveedores');
        
        // 2. Forzar recarga con loading visible en la lista
        await loadProveedores(true);
        
        // 3. Notificar éxito
        import('react-hot-toast').then(m => m.default.success("Lista de proveedores actualizada"));
    };

    const filteredProveedores = useMemo(() => {
        return proveedores.filter((prov) => {
            const matchesSearch = prov.razon_social.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                prov.codigo.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                prov.ruc.toLowerCase().includes(debouncedSearch.toLowerCase());
            const matchesCalificacion = filterCalificacion === 'TODOS' || prov.calificacion === filterCalificacion;
            return matchesSearch && matchesCalificacion;
        });
    }, [proveedores, debouncedSearch, filterCalificacion]);

    const getCalificacionColor = (calificacion: string) => {
        switch (calificacion) {
            case 'A':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'B':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'C':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };




    const totalInversion = proveedores.reduce((acc, prov) => acc + (prov.metricas?.total_comprado || 0), 0);
    const avgPuntualidad = proveedores.length > 0
        ? proveedores.reduce((acc, prov) => acc + (prov.metricas?.porcentaje_puntualidad || 0), 0) / proveedores.length
        : 0;

    return (
        <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4">

            {/* KPI Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-4 shadow-lg shadow-indigo-200/50">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-4 translate-x-4" />
                    <div className="relative">
                        <p className="text-xs font-semibold text-indigo-200 uppercase tracking-wide mb-1">Total Proveedores</p>
                        <p className="text-3xl font-black text-white">{proveedores.length}</p>
                    </div>
                    <Package size={28} className="absolute bottom-3 right-4 text-indigo-300/50" />
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 shadow-lg shadow-emerald-200/50">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-4 translate-x-4" />
                    <div className="relative">
                        <p className="text-xs font-semibold text-emerald-100 uppercase tracking-wide mb-1">Inversión Total</p>
                        <p className="text-3xl font-black text-white">S/ {totalInversion.toLocaleString()}</p>
                    </div>
                    <TrendingUp size={28} className="absolute bottom-3 right-4 text-emerald-200/50" />
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 shadow-lg shadow-amber-200/50">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-4 translate-x-4" />
                    <div className="relative">
                        <p className="text-xs font-semibold text-amber-100 uppercase tracking-wide mb-1">Puntualidad Global</p>
                        <p className="text-3xl font-black text-white">{avgPuntualidad.toFixed(1)}%</p>
                    </div>
                    <CheckCircle2 size={28} className="absolute bottom-3 right-4 text-amber-200/50" />
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-4 shadow-lg shadow-purple-200/50">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-4 translate-x-4" />
                    <div className="relative">
                        <p className="text-xs font-semibold text-purple-200 uppercase tracking-wide mb-1">Ítems Catálogo</p>
                        <p className="text-3xl font-black text-white">{proveedores.reduce((acc, p) => acc + (p.total_items_cotizados || 0), 0)}</p>
                    </div>
                    <BarChart3 size={28} className="absolute bottom-3 right-4 text-purple-300/50" />
                </div>
            </div>

            {/* Toolbar: Filtros y Acciones */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    {/* Búsqueda */}
                    <div className="relative flex-1">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={16}
                        />
                        <input
                            type="text"
                            placeholder="Buscar proveedor por nombre, RUC o código..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 h-9 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all rounded-xl text-sm outline-none placeholder:text-gray-400"
                        />
                    </div>

                    {/* Filtro Calificación + Acciones */}
                    <div className="flex items-center gap-2 shrink-0">
                        {/* Pills de calificación */}
                        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl border border-gray-200">
                            {[
                                { id: 'TODOS', label: 'Todos', icon: Layers },
                                { id: 'A', label: 'Nivel A', icon: Award },
                                { id: 'B', label: 'Nivel B', icon: Zap },
                                { id: 'C', label: 'Nivel C', icon: AlertCircle },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setFilterCalificacion(tab.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                                        filterCalificacion === tab.id
                                            ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                                    }`}
                                >
                                    <tab.icon size={12} />
                                    <span className="hidden sm:inline">{tab.label}</span>
                                    <span className="sm:hidden">{tab.id}</span>
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            title="Recargar proveedores"
                            className="h-9 w-9 flex items-center justify-center bg-white border border-gray-200 text-gray-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all rounded-xl shadow-sm active:scale-95"
                        >
                            <RefreshCcw size={15} className={loading ? 'animate-spin' : ''} />
                        </button>

                        <button
                            onClick={() => {
                                setSelectedProveedor(null);
                                setShowModal(true);
                            }}
                            className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-md shadow-indigo-200/50 flex items-center gap-2 font-semibold text-xs transition-all hover:shadow-lg active:scale-95"
                        >
                            <Plus size={15} />
                            <span>Nuevo Proveedor</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabla de Proveedores */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-dashed border-gray-200">
                    <div className="relative mb-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Building2 size={18} className="text-indigo-400 animate-pulse" />
                        </div>
                    </div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Sincronizando proveedores...</p>
                </div>
            ) : filteredProveedores.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Contador */}
                    <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/50 flex items-center justify-between">
                        <p className="text-xs text-gray-500 font-medium">
                            {filteredProveedores.length} proveedor{filteredProveedores.length !== 1 ? 'es' : ''} encontrado{filteredProveedores.length !== 1 ? 's' : ''}
                        </p>
                        {filterCalificacion !== 'TODOS' && (
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${getCalificacionColor(filterCalificacion)}`}>
                                Nivel {filterCalificacion}
                            </span>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Proveedor</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">RUC</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Nivel</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Puntualidad</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Calidad</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">Especialización</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Entrega</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredProveedores.map((proveedor) => (
                                    <tr
                                        key={proveedor.id}
                                        className="hover:bg-indigo-50/30 transition-colors group"
                                    >
                                        {/* Proveedor */}
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0">
                                                    <Building2 size={15} />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 text-sm leading-tight group-hover:text-indigo-700 transition-colors">
                                                        {proveedor.razon_social}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 font-mono">{proveedor.codigo}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* RUC */}
                                        <td className="px-4 py-3">
                                            <span className="text-xs font-mono text-gray-600">{proveedor.ruc}</span>
                                        </td>

                                        {/* Nivel */}
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-black text-sm border shadow-sm ${getCalificacionColor(proveedor.calificacion)}`}>
                                                {proveedor.calificacion}
                                            </span>
                                        </td>

                                        {/* Puntualidad */}
                                        <td className="px-4 py-3 hidden lg:table-cell">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                                    <div
                                                        className={`h-1.5 rounded-full transition-all ${
                                                            proveedor.metricas.porcentaje_puntualidad >= 90 ? 'bg-emerald-500'
                                                            : proveedor.metricas.porcentaje_puntualidad >= 70 ? 'bg-amber-500'
                                                            : 'bg-rose-500'
                                                        }`}
                                                        style={{ width: `${proveedor.metricas.porcentaje_puntualidad}%` }}
                                                    />
                                                </div>
                                                <span className={`text-xs font-bold ${
                                                    proveedor.metricas.porcentaje_puntualidad >= 90 ? 'text-emerald-600'
                                                    : proveedor.metricas.porcentaje_puntualidad >= 70 ? 'text-amber-600'
                                                    : 'text-rose-600'
                                                }`}>
                                                    {proveedor.metricas.porcentaje_puntualidad.toFixed(0)}%
                                                </span>
                                            </div>
                                        </td>

                                        {/* Calidad */}
                                        <td className="px-4 py-3 hidden lg:table-cell">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                                    <div
                                                        className="h-1.5 rounded-full bg-indigo-500"
                                                        style={{ width: `${(proveedor.metricas.calidad_promedio / 5) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-indigo-600">
                                                    {proveedor.metricas.calidad_promedio.toFixed(1)}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Especialización */}
                                        <td className="px-4 py-3 hidden xl:table-cell">
                                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                {proveedor.especializacion?.slice(0, 2).map((esp, idx) => (
                                                    <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-md border border-slate-200/80 truncate max-w-[90px]">
                                                        {esp}
                                                    </span>
                                                ))}
                                                {(proveedor.especializacion?.length || 0) > 2 && (
                                                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-md border border-indigo-100">
                                                        +{proveedor.especializacion.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Entrega */}
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <div className="flex items-center gap-1 text-xs">
                                                <Clock4 size={12} className="text-gray-400" />
                                                <span className="font-semibold text-gray-700">{proveedor.dias_entrega_promedio}d</span>
                                            </div>
                                        </td>

                                        {/* Acciones */}
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => {
                                                        setProveedorDetalleId(proveedor.id);
                                                        setShowDetalle(true);
                                                    }}
                                                    className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-indigo-600 transition-all"
                                                >
                                                    Ver Perfil
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedProveedor(proveedor);
                                                        setShowModal(true);
                                                    }}
                                                    className="h-8 w-8 rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all flex items-center justify-center"
                                                    title="Editar Proveedor"
                                                >
                                                    <Edit3 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-2xl mb-3">
                        <Package size={32} className="text-gray-300" />
                    </div>
                    <h3 className="text-base font-bold text-gray-700 mb-1">No se encontraron proveedores</h3>
                    <p className="text-sm text-gray-400 max-w-xs text-center">
                        Intenta{' '}
                        <button onClick={() => { setSearchTerm(''); setFilterCalificacion('TODOS'); }} className="text-indigo-600 hover:underline font-medium">ajustar</button>
                        {' '}los filtros de búsqueda o agrega un nuevo proveedor al sistema.
                    </p>
                </div>
            )}



            {/* Modal de Proveedor */}
            <ProveedorModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedProveedor(null);
                }}
                onSuccess={loadProveedores}
                proveedor={selectedProveedor}
            />

            {/* Modal de Detalle de Proveedor */}
            {showDetalle && proveedorDetalleId && (
                <DetalleProveedor
                    proveedorId={proveedorDetalleId}
                    onClose={() => {
                        setShowDetalle(false);
                        setProveedorDetalleId(null);
                    }}
                />
            )}
        </div>
    );
};

export default GestionProveedores;
